import { create } from 'zustand';
import { errorMessage, fetchElecDetail, fetchUtMatrixList } from '../api';
import { MAX_COMPARE_ELEC } from '../constants';
import {
  ColSort,
  ColumnFilters,
  ElecDiffRow,
  ElecSourceRow,
  TypeFilter,
  UtMatrixOption,
} from '../types';
import { compareElec } from '../utils/compareElec';

interface UtComparisonElecState {
  // ── ① 비교 대상 UT Matrix 선택 (전/후 정확히 2개) ──
  selectedMatrices: UtMatrixOption[];
  availableMatrices: UtMatrixOption[];
  loadingList: boolean;

  // ── 분석 결과 ──
  sourceBefore: ElecSourceRow[];
  sourceAfter: ElecSourceRow[];
  diffRows: ElecDiffRow[] | null;
  analyzedMatrices: UtMatrixOption[];
  hasAnalyzed: boolean;
  analyzing: boolean;
  /** "UT Matrix 정합화 Report"에서 불러온 이력의 RPT_ID — 새로 분석하면 null로 되돌아간다 */
  historyRptId: string | null;

  // ── ⑤ 변경내역 상세표 필터/정렬/열숨김 ──
  typeFilter: TypeFilter;
  columnFilters: ColumnFilters;
  sort: ColSort;
  hiddenCols: Set<string>;

  // ── actions ──
  fetchAvailableMatrices: () => Promise<void>;
  addMatrix: (m: UtMatrixOption) => void;
  removeMatrix: (utmId: string) => void;
  runAnalyze: () => Promise<void>;
  setHistoryRptId: (rptId: string | null) => void;
  loadHistorySnapshot: (params: {
    diffRows: ElecDiffRow[];
    analyzedMatrices: UtMatrixOption[];
    historyRptId: string;
  }) => void;
  setTypeFilter: (f: TypeFilter) => void;
  setColumnFilter: (colId: string, values: Set<string> | null) => void;
  setSort: (colId: string, dir: 'asc' | 'desc' | null) => void;
  toggleHiddenCol: (colId: string) => void;
  clearAllFilters: () => void;
  reset: () => void;
}

const EMPTY_SORT: ColSort = { col: null, dir: 'asc' };

export const useUtComparisonElecStore = create<UtComparisonElecState>((set, get) => ({
  selectedMatrices: [],
  availableMatrices: [],
  loadingList: false,

  sourceBefore: [],
  sourceAfter: [],
  diffRows: null,
  analyzedMatrices: [],
  hasAnalyzed: false,
  analyzing: false,
  historyRptId: null,

  typeFilter: '전체',
  columnFilters: {},
  sort: EMPTY_SORT,
  hiddenCols: new Set(),

  fetchAvailableMatrices: async () => {
    set({ loadingList: true });
    try {
      const list = await fetchUtMatrixList();
      set({ availableMatrices: list });
    } catch (e) {
      window.alert('UT Matrix 목록 조회 오류: ' + errorMessage(e));
    } finally {
      set({ loadingList: false });
    }
  },

  addMatrix: (m) =>
    set((s) => {
      if (s.selectedMatrices.length >= MAX_COMPARE_ELEC) {
        window.alert(`비교 대상 UT Matrix는 정확히 ${MAX_COMPARE_ELEC}개까지 선택할 수 있습니다.`);
        return s;
      }
      if (s.selectedMatrices.some((x) => x.utmId === m.utmId)) return s;
      return { selectedMatrices: [...s.selectedMatrices, m] };
    }),

  removeMatrix: (utmId) =>
    set((s) => ({ selectedMatrices: s.selectedMatrices.filter((m) => m.utmId !== utmId) })),

  runAnalyze: async () => {
    const { selectedMatrices } = get();
    if (selectedMatrices.length !== MAX_COMPARE_ELEC) {
      window.alert(`❌ 비교할 UT Matrix를 정확히 ${MAX_COMPARE_ELEC}개 선택해주세요.`);
      return;
    }
    set({ analyzing: true });
    try {
      const [before, after] = await Promise.all([
        fetchElecDetail([selectedMatrices[0].utmId]),
        fetchElecDetail([selectedMatrices[1].utmId]),
      ]);
      const diffRows = compareElec(before, after);
      set({
        sourceBefore: before,
        sourceAfter: after,
        diffRows,
        analyzedMatrices: selectedMatrices,
        hasAnalyzed: true,
        historyRptId: null,
        typeFilter: '전체',
        columnFilters: {},
        sort: EMPTY_SORT,
        hiddenCols: new Set(),
      });
    } catch (e) {
      window.alert('분석 오류: ' + errorMessage(e));
    } finally {
      set({ analyzing: false });
    }
  },

  setHistoryRptId: (historyRptId) => set({ historyRptId }),

  loadHistorySnapshot: ({ diffRows, analyzedMatrices, historyRptId }) =>
    set({
      selectedMatrices: analyzedMatrices,
      analyzedMatrices,
      sourceBefore: diffRows.map((r) => r.before).filter((r): r is ElecSourceRow => r !== null),
      sourceAfter: diffRows.map((r) => r.after).filter((r): r is ElecSourceRow => r !== null),
      diffRows,
      hasAnalyzed: true,
      historyRptId,
      typeFilter: '전체',
      columnFilters: {},
      sort: EMPTY_SORT,
      hiddenCols: new Set(),
    }),

  setTypeFilter: (typeFilter) => set({ typeFilter }),

  setColumnFilter: (colId, values) =>
    set((s) => {
      const next = { ...s.columnFilters };
      if (values === null) delete next[colId as keyof ColumnFilters];
      else next[colId as keyof ColumnFilters] = values;
      return { columnFilters: next };
    }),

  setSort: (colId, dir) =>
    set({ sort: dir === null ? EMPTY_SORT : { col: colId as ColSort['col'], dir } }),

  toggleHiddenCol: (colId) =>
    set((s) => {
      const next = new Set(s.hiddenCols);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return { hiddenCols: next };
    }),

  clearAllFilters: () => set({ columnFilters: {}, sort: EMPTY_SORT, typeFilter: '전체' }),

  reset: () =>
    set({
      selectedMatrices: [],
      sourceBefore: [],
      sourceAfter: [],
      diffRows: null,
      analyzedMatrices: [],
      hasAnalyzed: false,
      historyRptId: null,
      typeFilter: '전체',
      columnFilters: {},
      sort: EMPTY_SORT,
      hiddenCols: new Set(),
    }),
}));
